import React, { useMemo, useState } from "react";
import { PAGES_ENDPOINTS } from "../../config/api";
import { StateBadge } from "./StateBadge"
import { TournamentEvent, TournamentResult, ApiTournamentEvent, StateToInt } from "../../types"
import { updateTournamentEvent, userLabel } from "../../api";
import { Button } from "../common/Button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

type Props = {
  tournamentEvent: TournamentEvent;
};

export const TournamentEventCard: React.FC<Props> = ({ tournamentEvent }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState<TournamentEvent>(tournamentEvent);
  const formatDate = (d: string) => {
    // d: "YYYY-MM-DD"
    try {
      const [y, m, day] = d.split("-").map(Number);
      const dt = new Date(y, (m ?? 1) - 1, day ?? 1);
      return dt.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return d;
    }
  };

  const formatTime = (t: string) => (t ? t.slice(0, 5) : ""); // "HH:MM"

  const locationLabel = (() => {
    const l = event.location || {};
    return l.name || [l.address, l.city].filter(Boolean).join(", ") || "—";
  })();

  const top3: Record<number, TournamentResult[]> = React.useMemo(() => {
    if (event.state !== "concluso" || !Array.isArray(event.results)) return [];
    // Ordina per posizione asc, poi score desc
    const matrix: Record<number, TournamentResult[]> = {};
    for (const r of event.results) {
      if (!r.position || r.position > 3) continue;
      if (!matrix[r.position]) matrix[r.position] = [];
      matrix[r.position].push(r);
    }
    return matrix;
  }, [event.state, event.results]);


  const confirmEvent = async () => {
    const payload: Partial<ApiTournamentEvent> = {
      state: "confermato"
    };
    const event_res = await updateTournamentEvent(event.id, payload);
    setEvent(event_res)
  }

  const showConfermaButton: boolean = useMemo(() => {
    if (!user || !(user.can_manage_all || user.can_manage_content)) {
      return false;
    }
    const { state, location, date, time } = event;

    return (
      StateToInt[state] < StateToInt["confermato"] &&
      !!location?.id &&
      date.length > 0 &&
      time.length > 0
    );
  }, [event, user]);

  return (
    <article className="w-full rounded-xl border border-gray-300 bg-white p-4 shadow-sm hover:shadow-md transition-shadow justify-between flex flex-col">
      {/* Header */}
      <div>
        {/* Nome torneo + stato */}
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-base sm:text-lg font-semibold">
            {event.tournament.name}
          </h3>
          <StateBadge stato={event.state} size="xs" />
        </div>

        {/* Meta info */}
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
          <div className="flex items-center gap-2">
            <span className="inline-flex min-w-16 text-gray-500">Data</span>
            <span>{event.date && event.date.length > 0 ? formatDate(event.date) : "Da programmare"}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex min-w-16 text-gray-500">Ora</span>
            <span>{event.time && event.time.length > 0 ? formatTime(event.time) : "—"}</span>
          </div>
          <div className="flex items-center gap-2 sm:col-span-2">
            <span className="inline-flex min-w-16 text-gray-500">Luogo</span>
            <span className="truncate">{locationLabel}</span>
          </div>
        </div>
      </div>

      {/* Top 3 se concluso */}
      {Object.keys(top3).length > 0 && (
        <div className="mt-3">
          <h4 className="text-sm font-semibold text-gray-800 mb-2">Top 3</h4>
          <ol className="space-y-1">
            {Object.keys(top3).map((_, idx) => {
              const r: TournamentResult = top3[idx + 1][0];
              return (
                <li key={r.position} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span>
                      {idx === 0 ? "🥇" : idx === 1 ? "🥈" : "🥉"}
                    </span>
                    <span className="font-medium">{top3[idx + 1].map((r, i) => {
                      return (
                        <span key={`${r.id}-${idx}`}>
                          {userLabel(r.user)}
                          {top3[idx + 1].length > i + 1 ? ` - ` : ""}
                        </span>
                      )
                    })}</span>
                  </div>
                  <div className="text-gray-600">
                    {r.score}pt
                  </div>
                </li>
              )
            })}
          </ol>
        </div>
      )}

      {/* Footer actions */}
      <div className="grid mt-2 grid-cols-2 gap-2">
        <div className="flex flex-col">
          <Button 
            variant="primary"
            size="sm"
            onClick={() => navigate(PAGES_ENDPOINTS.TORNEO_DETAIL(event.id.toString()))}
          >
            Dettagli
          </Button>
        </div>
        {showConfermaButton && (
          <div className="flex flex-col">
            <Button
              variant="success"
              size="sm"
              onClick={confirmEvent}
            >
              Conferma
            </Button>
          </div>
        )}
      </div>
    </article>
  );
};
